import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";

/** Wraps protected routes. Routing decisions:
 *
 *  - while we haven't heard back from /auth/status yet → render a small
 *    loading screen (avoids flashing the login page).
 *  - JWT not configured server-side → /auth/disabled (an explainer page).
 *  - JWT set but no user → /setup (first-user bootstrap form).
 *  - logged in → render the protected children.
 *  - otherwise → /login (preserves the intended destination in state).
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status, ready } = useAuth();
  const location = useLocation();

  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-page text-fg-muted">
        <p className="text-sm">loading…</p>
      </div>
    );
  }

  if (status === null || !status.jwt_configured) {
    return <Navigate to="/auth/disabled" replace />;
  }

  if (status.user_count === 0) {
    return <Navigate to="/setup" replace />;
  }

  if (status.current_user == null) {
    return (
      <Navigate to="/login" replace state={{ from: location.pathname }} />
    );
  }

  return <>{children}</>;
}
